import { BUSINESS_TIMEZONE, toCalendarDateStringInTimeZone } from '@/lib/timezone'

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Dashboard default: full previous calendar month in the given timezone.
 * e.g. If today is 3 Mar or 29 Mar, default range is 1 Feb – 28/29 Feb.
 */
export function getDashboardDefaultDateRangeForTimezone(timeZone: string): { from: string; to: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date())
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {} as Record<string, string>)
  const today = `${parts.year}-${parts.month}-${parts.day}`
  const [y, m] = today.split('-').map(Number)

  const lastYear = m === 1 ? y - 1 : y
  const lastMonth = m === 1 ? 12 : m - 1

  const daysInMonth = (year: number, month: number): number => {
    if (month === 2) {
      const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
      return isLeap ? 29 : 28
    }
    if ([4, 6, 9, 11].includes(month)) return 30
    return 31
  }

  const from = `${lastYear}-${pad2(lastMonth)}-01`
  const to = `${lastYear}-${pad2(lastMonth)}-${pad2(daysInMonth(lastYear, lastMonth))}`
  return { from, to }
}

/**
 * Range for scheduled `refresh_analytics` / summary repair: **first day of the previous calendar month**
 * through **today** in the shop timezone.
 *
 * The dashboard default view is “previous month only”, but merchants often pick **current month** dates.
 * Cron used to refresh only the previous month, so `shop_daily_metrics` for “this month” never updated.
 * This span keeps the default month complete while always including the partial current month (max ~62 days).
 */
export function getExtendedAnalyticsRefreshRangeForTimezone(timeZone: string): { from: string; to: string } {
  const prev = getDashboardDefaultDateRangeForTimezone(timeZone)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date())
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {} as Record<string, string>)
  const today = `${parts.year}-${parts.month}-${parts.day}`
  return { from: prev.from, to: today }
}

/** Backwards-compatible helper. */
export function getDashboardDefaultDateRange(): { from: string; to: string } {
  return getDashboardDefaultDateRangeForTimezone(BUSINESS_TIMEZONE)
}

/** Format date for period labels: DD-MMM-YYYY in business timezone. */
export function formatPeriodLabel(dateStr: string, timeZone = BUSINESS_TIMEZONE): string {
  // dateStr is YYYY-MM-DD; use midday UTC so formatted day remains stable.
  const d = new Date(`${dateStr}T12:00:00.000Z`)
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

/** IANA zone from bootstrap (MerchantProfile / Shopify); fallback Asia/Karachi if missing or invalid. */
export function resolveShopDisplayTimeZone(bootstrapTimeZone?: string | null): string {
  const z = (bootstrapTimeZone ?? '').trim()
  if (!z) return BUSINESS_TIMEZONE
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: z })
    return z
  } catch {
    return BUSINESS_TIMEZONE
  }
}

const STRICT_YMD = /^\d{4}-\d{2}-\d{2}$/

/**
 * Format a summary/range YYYY-MM-DD key in the given zone (matches how drill-down range params are interpreted with shop TZ).
 */
export function formatBusinessYmdLabel(ymd: string, timeZone: string): string {
  const s = ymd.trim()
  if (!STRICT_YMD.test(s)) return ymd
  return formatPeriodLabel(s, timeZone)
}

/**
 * `createdAt` ISO instant → label for the **calendar day in shop TZ** (same idea as daily summary bucketing).
 */
export function formatCreatedInstantInShopDayLabel(iso: string, timeZone: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const ymd = toCalendarDateStringInTimeZone(d, timeZone)
  return formatPeriodLabel(ymd, timeZone)
}

/**
 * Table row label: if it is a strict YYYY-MM-DD summary key, format in shop TZ; otherwise leave unchanged (e.g. rolled-up bucket titles).
 */
export function formatMetricBreakdownRowLabel(label: string, timeZone: string): string {
  const s = label.trim()
  if (!STRICT_YMD.test(s)) return label
  return formatPeriodLabel(s, timeZone)
}
